import { useCallback } from 'react';
import { useSearchParams } from 'react-router';

export const DATE_RANGES = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'qtd', label: 'Quarter to date' },
  { value: 'ytd', label: 'Year to date' },
] as const;

export type DateRangeValue = (typeof DATE_RANGES)[number]['value'];

const DEFAULT_RANGE: DateRangeValue = '30d';
const RANGE_PARAM = 'range';
const PROJECT_PARAM = 'projectId';
const REGION_PARAM = 'regionId';

function isDateRangeValue(value: string | null): value is DateRangeValue {
  return DATE_RANGES.some((option) => option.value === value);
}

export function useGlobalFilters() {
  const [params, setParams] = useSearchParams();
  const rawRange = params.get(RANGE_PARAM);
  const range: DateRangeValue = isDateRangeValue(rawRange) ? rawRange : DEFAULT_RANGE;
  const projectId = params.get(PROJECT_PARAM)?.trim() ?? '';
  const regionId = params.get(REGION_PARAM)?.trim() ?? '';
  const label = DATE_RANGES.find((option) => option.value === range)?.label ?? '';
  const isFiltered = range !== DEFAULT_RANGE;

  const update = useCallback(
    (values: { range?: DateRangeValue; projectId?: string; regionId?: string }) => {
      setParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          if (values.range !== undefined) {
            if (values.range === DEFAULT_RANGE) next.delete(RANGE_PARAM);
            else next.set(RANGE_PARAM, values.range);
          }
          if (values.projectId !== undefined) {
            const value = values.projectId.trim();
            if (value) next.set(PROJECT_PARAM, value);
            else next.delete(PROJECT_PARAM);
          }
          if (values.regionId !== undefined) {
            const value = values.regionId.trim();
            if (value) next.set(REGION_PARAM, value);
            else next.delete(REGION_PARAM);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const setRange = useCallback(
    (next: DateRangeValue) => update({ range: next }),
    [update],
  );
  const setProjectId = useCallback(
    (next: string) => update({ projectId: next }),
    [update],
  );
  const setRegionId = useCallback(
    (next: string) => update({ regionId: next }),
    [update],
  );
  const setScopes = useCallback(
    (next: { projectId?: string; regionId?: string }) => update(next),
    [update],
  );
  const clear = useCallback(() => setRange(DEFAULT_RANGE), [setRange]);
  const clearScopes = useCallback(
    () => update({ projectId: '', regionId: '' }),
    [update],
  );

  return {
    range,
    setRange,
    label,
    isFiltered,
    clear,
    options: DATE_RANGES,
    projectId,
    regionId,
    setProjectId,
    setRegionId,
    setScopes,
    clearScopes,
    isScopeFiltered: Boolean(projectId || regionId),
  };
}
